import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { NewsArticle } from '../../types/News';
import { formatLocalizedTextDisplay } from '../../utils/localizedText';
import { useLanguages } from '../../hooks/useLanguages';

interface PaginationInfo {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  limit: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
  nextPage: number | null;
  prevPage: number | null;
}

interface PaginatedResponse {
  data: NewsArticle[];
  pagination: PaginationInfo;
}

const formatDate = (dateString?: string | null) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleString();
};

const NewsList: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { languageCodes } = useLanguages();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [articleToDelete, setArticleToDelete] = useState<NewsArticle | null>(null);
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const { data: newsData, isLoading, error } = useQuery<PaginatedResponse>({
    queryKey: ['news', page, rowsPerPage],
    queryFn: async () => {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/news/paginated`, {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          orderBy: 'publishedAt',
          orderDirection: 'DESC',
          includeUnpublished: true,
        },
      });
      return response.data as PaginatedResponse;
    },
  });

  const articles: NewsArticle[] = newsData?.data ?? [];
  const pagination = newsData?.pagination || {
    currentPage: 1,
    totalPages: 0,
    totalCount: 0,
    limit: 10,
    hasNextPage: false,
    hasPrevPage: false,
    nextPage: null,
    prevPage: null,
  };

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await axios.delete(`${process.env.REACT_APP_API_URL}/news/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['news'] });
      setDeleteDialogOpen(false);
      setArticleToDelete(null);
      setNotification({
        open: true,
        message: t('news.articleDeleted'),
        severity: 'success',
      });
    },
    onError: () => {
      setNotification({
        open: true,
        message: t('news.deleteFailed'),
        severity: 'error',
      });
    },
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{t('news.errorLoading')}</Alert>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">{t('news.title')}</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/news/new')}
        >
          {t('news.addArticle')}
        </Button>
      </Box>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('news.articleTitle')}</TableCell>
                <TableCell align="center">{t('news.status')}</TableCell>
                <TableCell>{t('news.publishedAt')}</TableCell>
                <TableCell>{t('news.updatedAt')}</TableCell>
                <TableCell align="right">{t('locations.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {articles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    {t('news.noArticles')}
                  </TableCell>
                </TableRow>
              ) : (
                articles.map((article) => (
                  <TableRow key={article.id}>
                    <TableCell>{formatLocalizedTextDisplay(article.title, languageCodes)}</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={
                          article.isPublished
                            ? t('news.published')
                            : t('news.draft')
                        }
                        size="small"
                        color={article.isPublished ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell>{formatDate(article.publishedAt)}</TableCell>
                    <TableCell>{formatDate(article.updatedAt)}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/news/${article.id}/edit`)}
                        aria-label={t('common.edit')}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          setArticleToDelete(article);
                          setDeleteDialogOpen(true);
                        }}
                        aria-label={t('common.delete')}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={pagination.totalCount}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Paper>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t('news.confirmDelete')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('news.deleteConfirmation', {
              title: formatLocalizedTextDisplay(articleToDelete?.title, languageCodes),
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => articleToDelete && deleteMutation.mutate(articleToDelete.id)}
            disabled={deleteMutation.isPending}
          >
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification({ ...notification, open: false })}
      >
        <Alert
          severity={notification.severity}
          onClose={() => setNotification({ ...notification, open: false })}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NewsList;
